import { useState } from "react";
import { Share2, Target, Rocket, Palette, Printer, ArrowRight, Filter, Compass, Zap, Bot, ShoppingCart, Globe, GraduationCap } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const serviceKeys = [
  { key: "socialMedia", icon: Share2, link: "/alpha" },
  { key: "paidTraffic", icon: Target, link: "/trafego" },
  { key: "launches", icon: Rocket, link: "/lancamentos" },
  { key: "branding", icon: Palette, link: "/marcas" },
  { key: "print", icon: Printer, link: "/grafica" },
  { key: "funnels", icon: Filter, link: "/funis" },
  { key: "strategy", icon: Compass, link: "/estrategia" },
  { key: "directResponse", icon: Zap, link: "/direct-response" },
  { key: "automation", icon: Bot, link: "/ai" },
  { key: "ecommerce", icon: ShoppingCart, link: "/ecommerce" },
  { key: "websites", icon: Globe, link: "/sites" },
  { key: "mentoring", icon: GraduationCap, link: "/mentorias" },
];

const ServicesSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const services = serviceKeys.map((s) => ({
    icon: s.icon,
    title: t(`servicesSection.items.${s.key}.title`),
    description: t(`servicesSection.items.${s.key}.description`),
    link: s.link,
  }));

  return (
    <section className="relative py-24 md:py-32">
      <div className="gradient-line mb-24 md:mb-32" />

      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div id="servicos" className="scroll-mt-24" />
        <div className="max-w-xl">
          <p className="text-sm font-body font-medium text-primary uppercase tracking-wide flex items-center gap-2">
            <span className="led" />
            {t("servicesSection.tag")}
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold tracking-display text-foreground">
            {t("servicesSection.title")}
          </h2>
          <p className="mt-4 text-base font-body text-muted-foreground">
            {t("servicesSection.subtitle")}
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.slice(0, 2).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i} isHovered={hoveredIndex === i} onHover={() => setHoveredIndex(i)} onLeave={() => setHoveredIndex(null)} large />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.slice(2, 5).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 2} isHovered={hoveredIndex === i + 2} onHover={() => setHoveredIndex(i + 2)} onLeave={() => setHoveredIndex(null)} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.slice(5, 7).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 5} isHovered={hoveredIndex === i + 5} onHover={() => setHoveredIndex(i + 5)} onLeave={() => setHoveredIndex(null)} large />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.slice(7, 9).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 7} isHovered={hoveredIndex === i + 7} onHover={() => setHoveredIndex(i + 7)} onLeave={() => setHoveredIndex(null)} large />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.slice(9, 11).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 9} isHovered={hoveredIndex === i + 9} onHover={() => setHoveredIndex(i + 9)} onLeave={() => setHoveredIndex(null)} large />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6">
            {services.slice(11).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 11} isHovered={hoveredIndex === i + 11} onHover={() => setHoveredIndex(i + 11)} onLeave={() => setHoveredIndex(null)} large />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

interface ServiceCardProps {
  service: { icon: any; title: string; description: string; link?: string };
  index: number;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  large?: boolean;
}

const ServiceCard = ({ service, isHovered, onHover, onLeave, large }: ServiceCardProps) => {
  const { t } = useTranslation();
  const Wrapper = service.link ? Link : "div";
  const wrapperProps = service.link ? { to: service.link } : {};

  return (
    <Wrapper
      {...(wrapperProps as any)}
      className={`group relative glass rounded-2xl overflow-hidden transition-all duration-500 ease-project block ${
        large ? "p-10 md:p-12" : "p-8 md:p-10"
      } ${
        isHovered
          ? "shadow-neon-md border-primary/40 scale-[1.02]"
          : "hover:shadow-neon-sm hover:border-primary/20"
      }`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 20% 80%, hsla(199, 76%, 52%, 0.08), transparent)" }} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-3/4 w-1/2 h-full transition-[left] duration-700 ease-project group-hover:left-[125%]" style={{ background: "linear-gradient(105deg, transparent 40%, hsla(199, 76%, 52%, 0.06) 45%, hsla(199, 76%, 52%, 0.12) 50%, hsla(199, 76%, 52%, 0.06) 55%, transparent 60%)" }} />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/40 transition-all duration-500" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="relative w-14 h-14 mb-6">
          <div className={`absolute inset-0 rounded-xl bg-primary/5 transition-all duration-500 ${isHovered ? "scale-110 bg-primary/10 shadow-neon-sm" : ""}`} />
          <div className="relative w-full h-full rounded-xl flex items-center justify-center">
            <service.icon className={`h-6 w-6 text-primary transition-transform duration-500 ${isHovered ? "scale-110" : ""}`} strokeWidth={1.5} />
          </div>
        </div>
        <h3 className={`font-display font-bold text-foreground ${large ? "text-xl" : "text-lg"}`}>{service.title}</h3>
        <p className={`mt-3 font-body text-muted-foreground leading-relaxed flex-1 ${large ? "text-base" : "text-sm"}`}>{service.description}</p>
        {service.link && (
          <div className="mt-6 flex items-center gap-2 text-sm font-body text-primary transition-all duration-300 opacity-100 translate-y-0 md:opacity-0 md:translate-y-1 md:group-hover:opacity-100 md:group-hover:translate-y-0">
            <span>{t("servicesSection.learnMore")}</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default ServicesSection;
